import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  TransferVehicleResponseDto,
  VehicleMergeCandidateDto,
  VehicleService,
  VehicleWithImagesResponseDto
} from '../../services/vehicle.service';
import { AlertService } from '../../services/alert.service';
import { StatefulComponent } from '../stateful/stateful.component';
import { UserSearchTypeaheadComponent } from '../user-search-typeahead/user-search-typeahead.component';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { UserDetail } from '../../models/UserDetail.model';

/**
 * Moves a vehicle into another customer's garage. Two steps: pick the new
 * owner, then — if that garage already holds what looks like the same car —
 * decide whether to merge the two records or keep them separate.
 *
 * Merging is irreversible, so it is never the default: when candidates exist
 * the user must choose explicitly, and the merge path gets a second confirm.
 */
@Component({
  selector: 'app-transfer-vehicle-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, UserSearchTypeaheadComponent, CdkScrollable],
  templateUrl: './transfer-vehicle-modal.component.html',
  styleUrls: ['./transfer-vehicle-modal.component.scss']
})
export class TransferVehicleModalComponent extends StatefulComponent {
  @Input() modalRef: NgbModalRef | undefined;
  @Input() vehicle: VehicleWithImagesResponseDto | undefined;
  @Output() transferred = new EventEmitter<TransferVehicleResponseDto>();

  step: 'owner' | 'confirm' = 'owner';
  selectedUser: UserDetail | null = null;
  candidates: VehicleMergeCandidateDto[] = [];
  // The candidate to merge into, or 'separate' to keep the vehicle on its own.
  // Null until the user chooses, which keeps the destructive path opt-in.
  mergeChoice: string | null = null;
  isLoadingCandidates = false;
  isSubmitting = false;

  constructor(
    private vehicleService: VehicleService,
    private alertService: AlertService,
    private ngbModal: NgbModal
  ) {
    super();
  }

  get vehicleName(): string {
    if (!this.vehicle) return 'this vehicle';
    const parts = [this.vehicle.Year, this.vehicle.Make, this.vehicle.Model].filter(Boolean);
    return parts.length ? parts.join(' ') : 'this vehicle';
  }

  get currentOwnerName(): string {
    if (!this.vehicle) return 'the current owner';
    const name = `${this.vehicle.CustFirstName ?? ''} ${this.vehicle.CustLastName ?? ''}`.trim();
    return name || this.vehicle.CustEmail || 'the current owner';
  }

  get newOwnerName(): string {
    if (!this.selectedUser) return '';
    const name = `${this.selectedUser.firstName ?? ''} ${this.selectedUser.lastName ?? ''}`.trim();
    return name || this.selectedUser.email || this.selectedUser.phoneNumber;
  }

  get canContinue(): boolean {
    return !!this.selectedUser && !this.isLoadingCandidates;
  }

  get canSubmit(): boolean {
    if (this.isSubmitting) return false;
    // With no look-alikes there is nothing to decide; otherwise the user must
    // say whether to merge before the button unlocks.
    return this.candidates.length === 0 || this.mergeChoice !== null;
  }

  get selectedCandidate(): VehicleMergeCandidateDto | undefined {
    return this.candidates.find(candidate => candidate.VehicleId === this.mergeChoice);
  }

  onUserSelected(user: UserDetail): void {
    this.selectedUser = user;
  }

  matchLabel(candidate: VehicleMergeCandidateDto): string {
    switch (candidate.MatchReason) {
      case 'VIN': return 'Same VIN';
      case 'PLATE': return 'Same plate';
      default: return 'Same year, make & model';
    }
  }

  candidateName(candidate: VehicleMergeCandidateDto): string {
    const parts = [candidate.Year, candidate.Make, candidate.Model].filter(Boolean);
    return parts.length ? parts.join(' ') : 'Vehicle';
  }

  /** Look for possible duplicates in the destination garage. */
  onContinue(): void {
    if (!this.vehicle || !this.selectedUser) return;

    if (this.selectedUser.userId === this.vehicle.CustUserId) {
      this.alertService.showSnackBar('This vehicle already belongs to that customer.');
      return;
    }

    this.isLoadingCandidates = true;
    this.mergeChoice = null;
    this.subs.push(
      this.vehicleService.getTransferCandidates(this.vehicle.VehicleId, this.selectedUser.userId).subscribe({
        next: (candidates) => {
          this.candidates = candidates;
          this.isLoadingCandidates = false;
          this.step = 'confirm';
        },
        error: (error) => {
          console.error('Failed to load merge candidates:', error);
          this.isLoadingCandidates = false;
          this.alertService.showSnackBar('Failed to check the destination garage. Please try again.');
        }
      })
    );
  }

  onBack(): void {
    this.step = 'owner';
    this.candidates = [];
    this.mergeChoice = null;
  }

  onSubmit(): void {
    if (!this.canSubmit) return;

    const candidate = this.selectedCandidate;
    if (!candidate) {
      this.submitTransfer(undefined);
      return;
    }

    // A merge retires one of the two records for good — confirm it separately.
    const confirmRef = this.ngbModal.open(ConfirmationModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    confirmRef.componentInstance.modalRef = confirmRef;
    confirmRef.componentInstance.title = 'Merge Vehicles';
    confirmRef.componentInstance.message =
      `Merge ${this.vehicleName} into ${this.newOwnerName}'s ${this.candidateName(candidate)}? ` +
      `Its images and work history move to the surviving vehicle, and the merged record is retired. ` +
      `This cannot be undone.`;
    confirmRef.componentInstance.confirmButtonText = 'Yes, Merge';
    confirmRef.componentInstance.cancelButtonText = 'Cancel';
    confirmRef.componentInstance.confirmButtonClass = 'btn-danger';

    confirmRef.result.then((confirmed) => {
      if (confirmed) {
        this.submitTransfer(candidate.VehicleId);
      }
    }).catch(() => {
      // Modal dismissed - do nothing
    });
  }

  private submitTransfer(mergeIntoVehicleId: string | undefined): void {
    if (!this.vehicle || !this.selectedUser) return;

    this.isSubmitting = true;
    this.subs.push(
      this.vehicleService.transferVehicle(this.vehicle.VehicleId, {
        NewCustUserId: this.selectedUser.userId,
        ...(mergeIntoVehicleId ? { MergeIntoVehicleId: mergeIntoVehicleId } : {})
      }).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.alertService.showSnackBar(
            response.Merged
              ? `Vehicles merged into ${this.newOwnerName}'s garage`
              : `${this.vehicleName} transferred to ${this.newOwnerName}`
          );
          this.transferred.emit(response);
          this.modalRef?.close();
        },
        error: (error) => {
          console.error('Transfer failed:', error);
          this.isSubmitting = false;
          this.alertService.showSnackBar('Failed to transfer vehicle. Please try again.');
        }
      })
    );
  }

  onCancel(): void {
    this.modalRef?.dismiss();
  }
}
